import Events from 'editor/components/Events';
import MoveMarkupInteraction from 'editor/components/interactions/MoveMarkupInteraction';
import { MapboxEventArgs } from 'editor/components/map/MapboxEvents/mapboxEventHandlers';
import MapSheetEvents from 'editor/components/map/MapboxEvents/MapSheetEvents';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import RFIPost from 'editor/components/RFIPost';
import { getSheetLayerHit } from 'editor/utils/map';
import { Role } from 'records/Role';

const startMove = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mousedown' | 'touchstart']>
): void => {
  // TODO: Add logic to only be able to move if you are an Admin

  const { world, evt } = args;
  const role = world.grab(Role)?.component;

  if (!role) {
    return;
  }

  world.locateAll([Markup, MarkupKit]).forEach((entity) => {
    const markupComp = entity.get(Markup);
    const { MKPoint } = entity.get(MarkupKit);

    if (!markupComp.selected) return;

    // Basically tells us if there is a Post RFI Interaction, so unless you are
    // creating or editing a markup, you shouldn't be able to move it
    const rfiPostEntity = world.locate(RFIPost);

    if (rfiPostEntity) {
      const rfiPostComp = rfiPostEntity.get(RFIPost);

      const sheetLayerEntity = world.getTagged(
        `sheetLayer-${rfiPostComp.pageid}`
      );

      if (!sheetLayerEntity) return;

      const { hit } = getSheetLayerHit(
        sheetLayerEntity,
        evt.lngLat.lng,
        evt.lngLat.lat
      );

      if (hit) {
        const eventsComp = sheetLayerEntity.get(Events);
        const point = new MKPoint(hit.x, hit.y);

        const isHit = markupComp.mkMarkup?.hit(point);

        if (isHit) {
          const { x, y } = point;
          const [left, bottom] = markupComp.mkMarkup?.getRect();

          entity.add(eventsComp).add(
            new MoveMarkupInteraction({
              originX: x,
              originY: y,
              currentX: x,
              currentY: y,
              deltaX: x - left,
              deltaY: y - bottom,
            })
          );
        }
      }
    }

    // Logic to move Punches.
    if (markupComp.isPunch && ['admin', 'member'].includes(role.role)) {
      const sheetLayerEntity = world.getTagged(
        `sheetLayer-${markupComp.markup.fileid}`
      );

      if (!sheetLayerEntity) return;

      const { hit } = getSheetLayerHit(
        sheetLayerEntity,
        evt.lngLat.lng,
        evt.lngLat.lat
      );

      if (hit) {
        const eventsComp = sheetLayerEntity.get(Events);
        const point = new MKPoint(hit.x, hit.y);

        const isHit = markupComp.mkMarkup?.hit(point);

        if (isHit) {
          const { x, y } = point;
          const [left, bottom] = markupComp.mkMarkup?.getRect();

          entity.add(eventsComp).add(
            new MoveMarkupInteraction({
              originX: x,
              originY: y,
              currentX: x,
              currentY: y,
              deltaX: x - left,
              deltaY: y - bottom,
            })
          );
        }
      }
    }
  });
};

const move = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mousemove' | 'touchmove']>
): void => {
  const { world, evt } = args;

  world.grabAll(MoveMarkupInteraction).forEach(({ entity, component }) => {
    const rfiPostEntity = world.locate(RFIPost);
    const markupComp = entity.get(Markup);

    if (rfiPostEntity) {
      const rfiPostComp = rfiPostEntity.get(RFIPost);

      const sheetLayerEntity = world.getTagged(
        `sheetLayer-${rfiPostComp.pageid}`
      );

      if (!sheetLayerEntity) return;

      const { hit } = getSheetLayerHit(
        sheetLayerEntity,
        evt.lngLat.lng,
        evt.lngLat.lat
      );

      if (hit) {
        // Does this need to be an MKPoint??
        const { MKPoint } = entity.get(MarkupKit);
        const { x, y } = new MKPoint(hit.x, hit.y);

        component.currentX = x;
        component.currentY = y;
      }
    }

    if (markupComp.selected && markupComp.isPunch) {
      const sheetLayerEntity = world.getTagged(
        `sheetLayer-${markupComp.markup.fileid}`
      );

      if (!sheetLayerEntity) return;

      const { hit } = getSheetLayerHit(
        sheetLayerEntity,
        evt.lngLat.lng,
        evt.lngLat.lat
      );

      if (hit) {
        // Does this need to be an MKPoint??
        const { MKPoint } = entity.get(MarkupKit);
        const { x, y } = new MKPoint(hit.x, hit.y);

        component.currentX = x;
        component.currentY = y;
      }
    }
  });
};

const endMove = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mouseup' | 'touchend']>
): void => {
  // Save moved Markup
  const { world } = args;

  world.locateAll(MoveMarkupInteraction).forEach((entity) => {
    const markupComp = entity.get(Markup);
    const { markup, mkMarkup } = markupComp;

    if (mkMarkup) {
      mkMarkup.setModDate(new Date().toISOString());
      const jsonBlob = JSON.stringify(mkMarkup.getAsObject());
      const nextMarkup = markup.set('jsonBlob', jsonBlob);

      if (entity.has(Events)) {
        const events = entity.get<Events<MapSheetEvents>>(Events);

        events.events.emit('markup:move', { markup: nextMarkup });
      }
    }

    entity.remove(MoveMarkupInteraction);
    entity.remove(Events);
  });
};

const moveMarkupEvents = {
  mousedown(args: MapboxEventArgs<mapboxgl.MapEventType['mousedown']>): void {
    startMove(args);
  },

  mousemove(args: MapboxEventArgs<mapboxgl.MapEventType['mousemove']>): void {
    move(args);
  },

  mouseup(args: MapboxEventArgs<mapboxgl.MapEventType['mouseup']>): void {
    endMove(args);
  },

  touchstart(args: MapboxEventArgs<mapboxgl.MapEventType['touchstart']>): void {
    startMove(args);
  },

  touchmove: (
    args: MapboxEventArgs<mapboxgl.MapEventType['touchmove']>
  ): void => {
    move(args);
  },

  touchend(args: MapboxEventArgs<mapboxgl.MapEventType['touchend']>): void {
    endMove(args);
  },
};

export default moveMarkupEvents;
